/**
 * ReviewerPublic — no-login, read-only Independent Reviewer Mode for an auditor.
 *
 *   Route: /reviewer/:token
 *
 * Fetches the consolidated reviewer summary via a magic-link token and renders
 * the same read-only ReviewerMode view a client's risk / audit function can open
 * without a login.
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AlertTriangle, ShieldCheck } from "lucide-react";
import axios from "axios";
import ReviewerMode from "../components/admin/ReviewerMode";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return iso; }
};

export default function ReviewerPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API_BASE}/public/reviewer/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "This reviewer link is invalid."));
  }, [token]);

  useEffect(() => {
    const client = data?.summary?.client;
    document.title = client ? `Independent Reviewer View — ${client}` : "Independent Reviewer View · Ekselens";
    return () => { document.title = "Ekselens Consulting"; };
  }, [data]);

  if (error) {
    return (
      <section className="min-h-[60vh] bg-white flex items-center justify-center px-4" data-testid="reviewer-public-error">
        <div className="max-w-md text-center">
          <AlertTriangle size={28} className="mx-auto text-amber-600" />
          <h1 className="ek-heading text-2xl text-black mt-4">{error}</h1>
          <p className="text-zinc-500 text-[14px] mt-2">Ask your Ekselens contact for a fresh link.</p>
        </div>
      </section>
    );
  }

  if (!data) {
    return <section className="min-h-[60vh] flex items-center justify-center text-zinc-400 text-[14px]" data-testid="reviewer-public-loading">Loading the reviewer view…</section>;
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-[#0B0B0C] text-white px-6 py-5">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-4">
            {data.share?.client_logo_url && (
              <>
                <img src={data.share.client_logo_url} alt="Client logo"
                  className="h-9 max-w-[150px] object-contain bg-white/95 rounded px-2 py-1" data-testid="reviewer-public-client-logo" />
                <span className="text-zinc-600">/</span>
              </>
            )}
            <div>
              <div className="text-[#A179FA] text-[10.5px] font-bold tracking-[0.22em]">EKSELENS CONSULTING</div>
              <h1 className="text-[20px] font-medium mt-1 inline-flex items-center gap-2">
                <ShieldCheck size={18} /> Independent Reviewer View
              </h1>
            </div>
          </div>
          <div className="text-[11px] text-zinc-400 text-right">
            Read-only · shared for review<br />
            {data.share?.expires_at && `Access expires ${fmtDate(data.share.expires_at)}`}
          </div>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 py-8">
        <ReviewerMode summary={data.summary} />
        <p className="text-[11px] text-zinc-400 mt-6 text-center">
          Prepared by Ekselens Consulting. This view is a read-only assurance summary and does not expose the
          underlying assessment answers or internal notes.
        </p>
      </main>
    </div>
  );
}
